import React, { memo, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { frontendToolHandler } from 'app/services/aiChat';
import { aiChatSignalRController } from 'app/services/signalR';
import {
  initAiChatConnection,
  teardownAiChatConnection,
} from 'app/services/signalR/aiChatConnector';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { logger } from 'utils/logger';

import {
  ensureSessionAction,
  getMessagesAction,
  loadUnreadCountAction,
  markChatReadAction,
} from 'store/aiChat/actions';
import { selectIsDrawerOpen } from 'store/aiChat/selectors';
import { selectMasterCompanyId } from 'store/auth/selectors';
import { selectIsAiChatEnabled } from 'store/companies/selectors';

import AiAssistantDrawer from './components/AiAssistantDrawer';
import AiAssistantLauncher from './components/AiAssistantLauncher';

const AiAssistant = ({ loanId }) => {
  const dispatch = useDispatch();
  const { getState } = useStore();
  const history = useHistory();
  const { applicationId } = useParams();
  const isAiChatAllowed = useSelector(selectIsAiChatEnabled);
  const isDrawerOpen = useSelector(selectIsDrawerOpen);
  const companyId = useSelector(selectMasterCompanyId);

  useEffect(() => {
    frontendToolHandler.setContext({ history, applicationId, companyId, loanId });
  }, [history, applicationId, companyId, loanId]);

  useEffectSafeStrictMode(() => {
    if (!isAiChatAllowed || !loanId || !companyId) {
      return;
    }

    const lifecycle = { isCancelled: false, joinedSessionId: null };

    const bootstrap = async () => {
      await initAiChatConnection(dispatch, getState);

      if (lifecycle.isCancelled) {
        return;
      }

      const session = await dispatch(ensureSessionAction({ loanId }));
      const sessionId = session?.sessionId ?? session?.id ?? null;

      if (lifecycle.isCancelled || !sessionId) {
        return;
      }

      lifecycle.joinedSessionId = sessionId;

      await aiChatSignalRController.joinSession(sessionId, companyId);
    };

    bootstrap().catch(error => {
      logger.error('[AiAssistant] Failed to bootstrap AI chat session', error);
    });

    return () => {
      lifecycle.isCancelled = true;

      const teardown = async () => {
        if (lifecycle.joinedSessionId) {
          await aiChatSignalRController.leaveSession(lifecycle.joinedSessionId);
        }

        await teardownAiChatConnection();
      };

      teardown().catch(error => {
        logger.error('[AiAssistant] Failed to tear down AI chat session', error);
      });
    };
  }, [dispatch, getState, isAiChatAllowed, loanId, companyId]);

  useEffectSafeStrictMode(() => {
    if (!isAiChatAllowed || !loanId) {
      return;
    }

    dispatch(loadUnreadCountAction({ loanId })).catch(error => {
      logger.error('[AiAssistant] Failed to load unread count', error);
    });
  }, [dispatch, isAiChatAllowed, loanId]);

  useEffectSafeStrictMode(() => {
    if (!isAiChatAllowed || !loanId) {
      return;
    }

    dispatch(getMessagesAction({ loanId })).catch(error => {
      logger.error('[AiAssistant] Failed to load chat history', error);
    });
  }, [dispatch, isAiChatAllowed, loanId]);

  useEffectSafeStrictMode(() => {
    if (!isAiChatAllowed || !loanId || !isDrawerOpen) {
      return;
    }

    dispatch(markChatReadAction({ loanId })).catch(error => {
      logger.error('[AiAssistant] Failed to mark chat as read', error);
    });
  }, [dispatch, isAiChatAllowed, isDrawerOpen, loanId]);

  if (!isAiChatAllowed) {
    return null;
  }

  return (
    <>
      <AiAssistantLauncher />
      <AiAssistantDrawer />
    </>
  );
};

AiAssistant.propTypes = {
  loanId: PropTypes.string,
};

export default memo(AiAssistant);
